import { useContext, useEffect, useState } from 'react'
import { isAxiosError } from 'axios'
import styled from 'styled-components'

import type { components } from '../../../constants/schema'

import { HttpReq, toast } from '../../../utils'

import { StoresContext } from '../../../provider/StoreProvider'

import { Wrap } from '../../../components/container/Wrap'
import { Content } from '../../../components/container/Content'
import { RowBetween } from '../../../components/common'
import { BankIcon } from '../../../components/bankIcon'
import type { BankName } from '../../../components/bankIcon/BankIcon'
import { Button } from '../../../components/button'
import { Text } from '../../../components/text'
import { Footer } from '../../../components/footer'
import { Header } from '../../../components/header'
import { NoticeContainer } from '../../../components/noticeContainer/NoticeContainer'
import { TitleContainer } from '../../../components/titleContainer/TitleContainer'

type MaskedWithdrawalAccount = components['schemas']['MaskedWithdrawalAccountDto']

export function WithdrawalAccount() {
  const {
    commonStore: { setShowTab, setInvalidAccess },
  } = useContext(StoresContext)

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [withdrawalAccount, setWithdrawalAccount] = useState<MaskedWithdrawalAccount | null>(null)

  useEffect(() => {
    setShowTab(false)
    loadData()
  }, [])

  const loadData = async () => {
    try {
      const withdrawalAccountResponse = await HttpReq<MaskedWithdrawalAccount>({
        method: 'GET',
        url: '/user/current/withdrawalAccount',
      })

      setWithdrawalAccount(withdrawalAccountResponse)
      setIsLoading(false)
    } catch (error) {
      if (isAxiosError(error)) {
        toast(error.response?.data.message, error, false)
      }
      setInvalidAccess(true)
    }
  }

  if (isLoading) {
    return <></>
  }

  return (
    <Wrap>
      <Header type="SUB" title="출금 계좌" />
      <Content backgroundColor="gray_1">
        <TitleContainer title="출금 계좌 정보">
          <BankInfoBox>
            <BankInfo>
              <BankIcon name={withdrawalAccount?.bankName as BankName} size={46} />
              <Text size={18} weight={600}>
                {withdrawalAccount?.bankName}
              </Text>
            </BankInfo>
          </BankInfoBox>
          <AccountList>
            <RowBetween>
              <Text size={14} weight={600} color="text_secondary">
                계좌번호
              </Text>
              <Text size={14} weight={500} lineHeight={22} color="text_secondary">
                {withdrawalAccount?.account}
              </Text>
            </RowBetween>
            <RowBetween>
              <Text size={14} weight={600} color="text_secondary">
                예금주
              </Text>
              <Text size={16} weight={500} lineHeight={22} color="text_secondary">
                {withdrawalAccount?.bankOwner}
              </Text>
            </RowBetween>
          </AccountList>
        </TitleContainer>
        <NoticeContainer
          title="안내사항"
          list={[
            '출금 계좌는 서비스 가입자 본인 명의의 실계좌만 가능해요.',
            '출금 계좌 등록 및 변경은 더 보기>내 정보>출금계좌에서 가능해요.',
            'NH농협은행 예치금 계좌로의 입금은 등록된 출금 계좌로만 가능해요.',
          ]}
        />
      </Content>
      <Footer>
        <Button
          type="primary"
          size="large"
          block={true}
          href="/account/withdrawal/banks"
          routeProps={{ state: { prevPathname: window.location.pathname } }}
        >
          변경하기
        </Button>
      </Footer>
    </Wrap>
  )
}

const BankInfoBox = styled(RowBetween)`
  margin-bottom: 24px;
`

const BankInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const AccountList = styled.div`
  display: flex;
  gap: 8px;
  flex-direction: column;
`
